import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@gwrctl/api-client";
import type {
  FeedbackAdminComment,
  FeedbackAdminDetail,
  FeedbackAdminPost,
  FeedbackAdminTab,
  FeedbackCategory,
  FeedbackPublicStatus,
  FeedbackRank,
  FeedbackStatus,
} from "@gwrctl/shared";
import { FEEDBACK_CATEGORIES, FEEDBACK_LIMITS, isPublicStatus } from "@gwrctl/shared";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { fmtAge } from "../lib/time.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Banner, Button, Card, Chips, ConfirmButton, Empty, ErrorState, Field, Modal } from "../components/Ui.tsx";

/**
 * Oyuncu geri bildirimi (Cinderpath). Oyuncunun yazdığı her şey önce "Bekleyen"e
 * düşer; onaylanınca oyundaki panoda herkese görünür. Ayrıntıda yazara özel
 * yazışma ve panoda görünen herkese açık cevap ayrı ayrı yazılır.
 *
 * Yorumlar ise onay beklemeden görünür; 3 farklı oyuncu bildirince kendiliğinden
 * gizlenir ve "Bildirilen yorumlar" sekmesine düşer (geri aç / kaldır).
 *
 * Metinler düz metin olarak gösterilir (React metin düğümü; HTML işlenmez).
 * Telefonda konsol bu sayfanın kendisini WebView'da açar.
 */

/** Sunucunun gönderi sekmeleri + yalnızca konsolda olan "bildirilen yorumlar". */
type Tab = FeedbackAdminTab | "comments";
const TABS: readonly Tab[] = ["pending", "board", "reported", "comments", "rejected"];
const PUBLIC: readonly FeedbackPublicStatus[] = ["open", "planned", "done"];

const CATEGORY: Record<FeedbackCategory, string> = { bug: "Hata", idea: "Öneri", other: "Diğer" };
const STATUS: Record<FeedbackStatus, string> = {
  pending: "bekliyor",
  open: "açık",
  planned: "planlandı",
  done: "yapıldı",
  rejected: "reddedildi",
};
const STATUS_TAG: Record<FeedbackStatus, string> = {
  pending: "tag-outline",
  open: "tag-neutral",
  planned: "tag-accent",
  done: "tag-accent",
  rejected: "tag-danger",
};

function rankLabel(rank: FeedbackRank): string {
  return typeof rank === "number" ? `rütbe ${rank}` : rank;
}

export function FeedbackPage(): ReactNode {
  const apps = useQuery({ queryKey: ["apps"], queryFn: () => client.apps() });
  const feedbackApps = (apps.data ?? []).filter((a) => a.panels.includes("feedback"));
  const [appId, setAppId] = useState<string>("cinderpath");
  const [tab, setTab] = useState<Tab>("pending");
  const [sort, setSort] = useState<"top" | "new">("new");
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (feedbackApps.length && !feedbackApps.some((a) => a.id === appId)) setAppId(feedbackApps[0]!.id);
  }, [feedbackApps, appId]);

  // Yorum sekmesinde gönderi listesi yerine bekleyenler çekilir (sekme sayaçları için).
  const postTab: FeedbackAdminTab = tab === "comments" ? "pending" : tab;
  const list = useQuery({
    queryKey: ["feedback", appId, postTab, sort],
    queryFn: () => client.feedbackPosts(appId, postTab, sort),
    refetchInterval: 60_000,
  });
  const counts = list.data?.counts;

  const tabLabels: Record<Tab, ReactNode> = {
    pending: `Bekleyen${counts?.pending ? ` · ${counts.pending}` : ""}`,
    board: "Pano",
    reported: `Bildirilen${counts?.reported ? ` · ${counts.reported}` : ""}`,
    comments: `Bildirilen yorumlar${counts?.flaggedComments ? ` · ${counts.flaggedComments}` : ""}`,
    rejected: "Reddedilen",
    all: "Tümü",
  };

  const rows = list.data?.items ?? [];

  return (
    <>
      <PageHeader
        kicker={appId}
        title="Geri bildirim"
        meta={
          counts?.unanswered
            ? `${counts.unanswered} yazışmada oyuncudan cevaplanmamış mesaj var`
            : "önce onay: onaylanan gönderi oyundaki panoda görünür"
        }
        actions={
          feedbackApps.length > 1 ? (
            <select className="input !w-auto" aria-label="Uygulama" value={appId} onChange={(e) => setAppId(e.target.value)}>
              {feedbackApps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />

      <PageBody>
        <div className="flex flex-wrap items-center gap-3">
          <Chips values={TABS} value={tab} onChange={setTab} labels={tabLabels} aria-label="Sekme" />
          {tab === "board" && (
            <div className="ml-auto">
              <Chips
                values={["top", "new"] as const}
                value={sort}
                onChange={setSort}
                labels={{ top: "En çok beğenilen", new: "En yeni" }}
                aria-label="Sıralama"
              />
            </div>
          )}
        </div>

        {tab === "comments" ? (
          <FlaggedComments appId={appId} onOpenPost={setOpen} />
        ) : (
          <Card
            title={list.data ? `${rows.length} gönderi` : "Gönderiler"}
            hint={tab === "pending" ? "en eski üstte" : tab === "reported" ? "son bildirilen üstte" : undefined}
            flush
          >
            {list.isError ? (
              <ErrorState error={list.error} onRetry={() => void list.refetch()} />
            ) : list.isLoading ? (
              <Empty>Yükleniyor…</Empty>
            ) : rows.length ? (
              rows.map((row) => <PostRow key={row.id} row={row} onOpen={() => setOpen(row.id)} />)
            ) : (
              <Empty>
                {tab === "pending"
                  ? "Bekleyen gönderi yok"
                  : tab === "reported"
                    ? "Bildirilen gönderi yok"
                    : tab === "board"
                      ? "Panoda gönderi yok"
                      : "Kayıt yok"}
              </Empty>
            )}
          </Card>
        )}
      </PageBody>

      {open && <DetailModal appId={appId} id={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function PostRow({ row, onOpen }: { row: FeedbackAdminPost; onOpen: () => void }): ReactNode {
  return (
    <button type="button" className="row row-hover w-full flex-wrap items-start gap-3 text-left" onClick={onOpen}>
      <div className="grid min-w-0 flex-1 basis-[240px] gap-1.5">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="tag tag-neutral">{CATEGORY[row.category]}</span>
          <span className={`tag ${STATUS_TAG[row.status]}`}>{STATUS[row.status]}</span>
          {row.devUnread && <span className="tag tag-accent">yeni mesaj</span>}
          {row.openReports > 0 && <span className="tag tag-danger">bildirildi ×{row.openReports}</span>}
          {row.authorBlocked && <span className="tag tag-danger">engelli yazar</span>}
          {row.publicReply && <span className="tag tag-outline">cevaplandı</span>}
        </span>
        <span className="line-clamp-3 text-sm break-words whitespace-pre-wrap text-neutral-100">{row.text}</span>
        <span className="text-2xs text-neutral-500">
          {row.heroName} · {rankLabel(row.rank)} · {fmtAge(Date.now() - row.createdAt)}
          {row.votes ? ` · ${row.votes} beğeni` : ""}
          {row.messageCount ? ` · ${row.messageCount} mesaj` : ""}
          {row.commentCount ? ` · ${row.commentCount} yorum` : ""}
          {row.appVersion ? ` · v${row.appVersion}` : ""}
          {row.locale ? ` · ${row.locale}` : ""}
        </span>
      </div>
    </button>
  );
}

function DetailModal({ appId, id, onClose }: { appId: string; id: string; onClose: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<"private" | "public">("private");
  const [draft, setDraft] = useState("");
  const [approveAs, setApproveAs] = useState<FeedbackPublicStatus>("open");
  const [totp, setTotp] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["feedback-post", appId, id],
    queryFn: () => client.feedbackPost(appId, id),
  });

  const refresh = (data?: FeedbackAdminDetail): void => {
    if (data) queryClient.setQueryData(["feedback-post", appId, id], data);
    void queryClient.invalidateQueries({ queryKey: ["feedback", appId] });
    void queryClient.invalidateQueries({ queryKey: ["feedback-counts"] });
  };

  // Ayrıntıyı açmak "yeni mesaj" bayrağını indiriyor: liste ve rozet tazelensin.
  useEffect(() => {
    if (detail.data) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.dataUpdatedAt]);

  const act = useMutation({
    mutationFn: async (fn: () => Promise<FeedbackAdminDetail | void>) => fn(),
    onSuccess: (data) => refresh(data ?? undefined),
    onError: (err) => toast({ tone: "danger", title: "İşlem yapılamadı", body: humanizeError(err) }),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (totp) await client.confirm(totp);
      return client.deleteFeedback(appId, id);
    },
    onSuccess: () => {
      toast({ tone: "ok", title: "Gönderi silindi" });
      refresh();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.needsTotp) {
        setTotp("");
        return;
      }
      toast({ tone: "danger", title: "Silinemedi", body: humanizeError(err) });
    },
  });

  const d = detail.data;
  const post = d?.post;

  useEffect(() => {
    if (mode === "public") setDraft(post?.publicReply?.text ?? "");
    else setDraft("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const busy = act.isPending || remove.isPending;
  const text = draft.trim();

  const send = (): void => {
    if (!text) return;
    act.mutate(async () => {
      const res =
        mode === "public"
          ? await client.replyFeedback(appId, id, text)
          : await client.messageFeedback(appId, id, text);
      if (mode === "private") setDraft("");
      toast({ tone: "ok", title: mode === "public" ? "Cevap panoda" : "Mesaj gönderildi" });
      return res;
    });
  };

  return (
    <Modal
      title={post ? `${post.heroName} · ${CATEGORY[post.category]}` : "Gönderi"}
      subtitle={post ? `${rankLabel(post.rank)} · ${new Date(post.createdAt).toLocaleString("tr-TR")}` : undefined}
      onClose={onClose}
      dismissable={!busy}
      width={680}
    >
      {detail.isError ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      ) : !d || !post ? (
        <Empty>Yükleniyor…</Empty>
      ) : (
        <div className="grid gap-4">
          {d.author.blocked && (
            <Banner tone="danger" kicker="engelli">
              Bu yazar engelli: yeni gönderi ve mesaj gönderemiyor.{d.author.blockReason ? ` (${d.author.blockReason})` : ""}
            </Banner>
          )}

          <div className="rounded-[8px] border border-neutral-800 bg-white/[0.02] px-3.5 py-3 text-sm leading-relaxed break-words whitespace-pre-wrap text-neutral-100">
            {post.text}
          </div>

          <p className="text-2xs text-neutral-500">
            <span className={`tag ${STATUS_TAG[post.status]} mr-1.5`}>{STATUS[post.status]}</span>
            {post.votes} beğeni · {d.author.postCount} gönderi bu yazardan
            {post.appVersion ? ` · v${post.appVersion}` : ""}
            {post.locale ? ` · ${post.locale}` : ""}
            {post.reviewNote ? ` · not: ${post.reviewNote}` : ""}
          </p>

          {/* --- Tür (oyuncu yanlış seçtiyse düzelt) --- */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="label mb-0">Tür</span>
            <Chips
              values={FEEDBACK_CATEGORIES}
              value={post.category}
              onChange={(c) => {
                if (c !== post.category) act.mutate(() => client.setFeedbackCategory(appId, id, c));
              }}
              labels={CATEGORY}
              aria-label="Tür"
            />
          </div>

          {/* --- Durum --- */}
          {post.status === "pending" || post.status === "rejected" ? (
            <div className="flex flex-wrap items-center gap-2">
              <Chips
                values={PUBLIC}
                value={approveAs}
                onChange={setApproveAs}
                labels={{ open: "açık", planned: "planlandı", done: "yapıldı" }}
                aria-label="Onaylanınca durum"
              />
              <Button
                variant="primary"
                className="ml-auto"
                loading={act.isPending}
                onClick={() => act.mutate(() => client.approveFeedback(appId, id, approveAs))}
              >
                Onayla · panoya çıkar
              </Button>
              {post.status === "pending" && (
                <Button disabled={busy} onClick={() => act.mutate(() => client.rejectFeedback(appId, id))}>
                  Reddet
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="label mb-0">Durum</span>
              <Chips
                values={PUBLIC}
                value={post.status}
                onChange={(s) => act.mutate(() => client.setFeedbackStatus(appId, id, s))}
                labels={{ open: "açık", planned: "planlandı", done: "yapıldı" }}
                aria-label="Durum"
              />
              <Button
                variant="ghost"
                className="ml-auto"
                disabled={busy}
                onClick={() => act.mutate(() => client.rejectFeedback(appId, id))}
              >
                Panodan kaldır (reddet)
              </Button>
            </div>
          )}

          {/* --- Herkese açık cevap --- */}
          {post.publicReply && (
            <div className="grid gap-1.5 rounded-[8px] border border-accent-800/60 bg-accent-900/30 px-3.5 py-2.5">
              <span className="kicker">herkese açık cevap · {fmtAge(Date.now() - post.publicReply.at)}</span>
              <span className="text-sm break-words whitespace-pre-wrap text-neutral-100">{post.publicReply.text}</span>
              <div>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => act.mutate(() => client.deleteFeedbackReply(appId, id))}>
                  Cevabı kaldır
                </Button>
              </div>
            </div>
          )}

          {/* --- Yazışma --- */}
          <div className="grid gap-2">
            <span className="label mb-0">Yazışma (yalnızca yazar görür)</span>
            {d.messages.length ? (
              <div className="grid max-h-72 gap-2 overflow-y-auto pr-1">
                {d.messages.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[85%] rounded-[8px] px-3 py-2 text-sm break-words whitespace-pre-wrap ${
                      m.from === "dev"
                        ? "justify-self-end bg-accent-900/60 text-neutral-100"
                        : "justify-self-start border border-neutral-800 bg-white/[0.03] text-neutral-200"
                    }`}
                  >
                    {m.text}
                    <div className="mt-1 text-2xs text-neutral-500">
                      {m.from === "dev" ? "sen" : post.heroName} · {new Date(m.at).toLocaleString("tr-TR")}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-neutral-600">Henüz mesaj yok.</p>
            )}
            {post.authorUnread && <p className="text-2xs text-neutral-500">Yazar son mesajını henüz okumadı.</p>}
          </div>

          {/* --- Cevap kutusu --- */}
          <div className="grid gap-2">
            <Chips
              values={["private", "public"] as const}
              value={mode}
              onChange={setMode}
              labels={{ private: "Yazara özel mesaj", public: "Herkese açık cevap" }}
              aria-label="Cevap türü"
            />
            <Field
              label={mode === "public" ? "Panoda gönderinin altında görünür" : "Yalnızca yazar görür"}
              hint={`${text.length}/${FEEDBACK_LIMITS.replyMax}`}
            >
              <textarea
                className="input min-h-24"
                maxLength={FEEDBACK_LIMITS.replyMax}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </Field>
            {mode === "public" && !isPublicStatus(post.status) && (
              <p className="text-2xs text-warn">Gönderi onaylanmadıkça cevap panoda görünmez; yazar yine görür.</p>
            )}
            <div className="flex justify-end">
              <Button variant="primary" loading={act.isPending} disabled={!text} onClick={send}>
                {mode === "public" ? (post.publicReply ? "Cevabı güncelle" : "Cevabı yayınla") : "Mesajı gönder"}
              </Button>
            </div>
          </div>

          {/* --- Yorumlar --- */}
          <CommentsSection appId={appId} postId={id} isPublic={isPublicStatus(post.status)} />

          {/* --- Bildirimler --- */}
          {d.reports.length > 0 && (
            <div className="grid gap-1.5">
              <span className="label mb-0">Bildirimler</span>
              {d.reports.map((r) => (
                <p key={r.id} className="text-xs text-neutral-300">
                  {r.resolvedAt ? <span className="text-neutral-600">(kapatıldı) </span> : null}
                  {r.reason || "neden yazılmamış"} · <span className="text-neutral-500">{fmtAge(Date.now() - r.at)}</span>
                </p>
              ))}
              {post.openReports > 0 && (
                <div>
                  <Button size="sm" disabled={busy} onClick={() => act.mutate(() => client.resolveFeedbackReports(appId, id))}>
                    Bildirimleri kapat
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* --- Tehlikeli --- */}
          <div className="flex flex-wrap items-center gap-2 border-t border-neutral-900 pt-3">
            {d.author.blocked ? (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  act.mutate(async () => {
                    await client.unblockFeedbackAuthor(appId, d.author.id);
                    return client.feedbackPost(appId, id);
                  })
                }
              >
                Engeli kaldır
              </Button>
            ) : (
              <ConfirmButton
                variant="ghost"
                confirmLabel="Engellensin mi?"
                disabled={busy}
                onConfirm={() =>
                  act.mutate(async () => {
                    const res = await client.blockFeedbackAuthor(appId, d.author.id, { rejectPending: true });
                    toast({
                      tone: "ok",
                      title: "Yazar engellendi",
                      body: res.rejected ? `${res.rejected} bekleyen gönderisi reddedildi` : undefined,
                    });
                    return client.feedbackPost(appId, id);
                  })
                }
              >
                Yazarı engelle
              </ConfirmButton>
            )}
            <span className="mono text-2xs text-neutral-600" title="yazar kimliği">
              {d.author.id.slice(0, 8)}…
            </span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {totp !== null && (
                <input
                  className="input mono !w-28"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="TOTP kodu"
                  aria-label="Doğrulama kodu"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                />
              )}
              <ConfirmButton
                confirmLabel="Kalıcı silinsin mi?"
                loading={remove.isPending}
                disabled={busy || (totp !== null && totp.length !== 6)}
                onConfirm={() => remove.mutate()}
              >
                Sil
              </ConfirmButton>
            </div>
          </div>
          {totp !== null && (
            <p className="text-2xs text-neutral-500 sm:text-right">Silme geri alınamaz; 6 haneli doğrulama kodunu gir.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

// ===================== Yorumlar =====================

const COMMENT_STATE: Record<FeedbackAdminComment["state"], { label: string; tag: string } | null> = {
  visible: null,
  hidden: { label: "incelemede · gizli", tag: "tag-danger" },
  removed: { label: "kaldırıldı", tag: "tag-outline" },
};

/** Tek yorum satırı: kaldır / geri aç / bildirimi kapat / yazarı engelle. */
function CommentItem({
  appId,
  c,
  showPost,
  onOpenPost,
  onChanged,
}: {
  appId: string;
  c: FeedbackAdminComment;
  showPost?: boolean;
  onOpenPost?: (postId: string) => void;
  onChanged: () => void;
}): ReactNode {
  const toast = useToast();
  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => onChanged(),
    onError: (err) => toast({ tone: "danger", title: "İşlem yapılamadı", body: humanizeError(err) }),
  });
  const state = COMMENT_STATE[c.state];
  const busy = act.isPending;

  return (
    <div className={`grid gap-1.5 px-3.5 py-2.5 ${c.state === "removed" ? "opacity-60" : ""}`}>
      {showPost && (
        <button
          type="button"
          className="line-clamp-1 text-left text-2xs text-neutral-500 hover:text-neutral-300"
          onClick={() => onOpenPost?.(c.postId)}
        >
          gönderi: {c.postExcerpt || "—"}
        </button>
      )}
      <span className="flex flex-wrap items-center gap-1.5">
        {c.fromDev && <span className="tag tag-accent">geliştirici</span>}
        {state && <span className={`tag ${state.tag}`}>{state.label}</span>}
        {c.openReports > 0 && <span className="tag tag-danger">bildirildi ×{c.openReports}</span>}
        {c.authorBlocked && <span className="tag tag-danger">engelli yazar</span>}
      </span>
      <span className="text-sm break-words whitespace-pre-wrap text-neutral-100">{c.text}</span>
      <span className="text-2xs text-neutral-500">
        {c.heroName}
        {c.fromDev ? "" : ` · ${rankLabel(c.rank)}`} · {fmtAge(Date.now() - c.createdAt)}
        {c.appVersion ? ` · v${c.appVersion}` : ""}
        {c.locale ? ` · ${c.locale}` : ""}
        {c.reportReasons.length ? ` · neden: ${c.reportReasons.join(", ")}` : ""}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {c.state !== "visible" ? (
          <Button size="sm" disabled={busy} onClick={() => act.mutate(() => client.restoreFeedbackComment(appId, c.id))}>
            Geri aç
          </Button>
        ) : c.openReports > 0 ? (
          <Button size="sm" disabled={busy} onClick={() => act.mutate(() => client.restoreFeedbackComment(appId, c.id))}>
            Bildirimi kapat
          </Button>
        ) : null}
        {c.state !== "removed" && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => act.mutate(() => client.removeFeedbackComment(appId, c.id))}>
            Kaldır
          </Button>
        )}
        {c.authorId && !c.authorBlocked && (
          <ConfirmButton
            size="sm"
            variant="ghost"
            confirmLabel="Engellensin mi?"
            disabled={busy}
            onConfirm={() =>
              act.mutate(async () => {
                const res = await client.blockFeedbackAuthor(appId, c.authorId!, { rejectPending: true });
                toast({
                  tone: "ok",
                  title: "Yazar engellendi",
                  body: res.rejected ? `${res.rejected} bekleyen gönderisi reddedildi` : undefined,
                });
              })
            }
          >
            Yazarı engelle
          </ConfirmButton>
        )}
      </div>
    </div>
  );
}

/** Gönderi ayrıntısında: yorumlar + geliştirici yorumu kutusu. */
function CommentsSection({ appId, postId, isPublic }: { appId: string; postId: string; isPublic: boolean }): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const comments = useQuery({
    queryKey: ["feedback-comments", appId, postId],
    queryFn: () => client.feedbackComments(appId, postId),
  });
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["feedback-comments", appId] });
    void queryClient.invalidateQueries({ queryKey: ["feedback-flagged", appId] });
    void queryClient.invalidateQueries({ queryKey: ["feedback-post", appId, postId] });
    void queryClient.invalidateQueries({ queryKey: ["feedback", appId] });
  };
  const write = useMutation({
    mutationFn: (text: string) => client.devFeedbackComment(appId, postId, text),
    onSuccess: () => {
      setDraft("");
      toast({ tone: "ok", title: "Yorum panoda" });
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Yorum yazılamadı", body: humanizeError(err) }),
  });
  const items = comments.data?.items ?? [];
  const text = draft.trim();

  return (
    <div className="grid gap-2">
      <span className="label mb-0">Yorumlar (panoda herkes görür; onay beklemez)</span>
      {comments.isError ? (
        <ErrorState error={comments.error} onRetry={() => void comments.refetch()} />
      ) : comments.isLoading ? (
        <p className="text-xs text-neutral-600">Yükleniyor…</p>
      ) : items.length ? (
        <div className="grid max-h-80 divide-y divide-neutral-900 overflow-y-auto rounded-[8px] border border-neutral-800">
          {items.map((c) => (
            <CommentItem key={c.id} appId={appId} c={c} onChanged={refresh} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-neutral-600">Henüz yorum yok.</p>
      )}
      <Field label="Geliştirici yorumu" hint={`${text.length}/${FEEDBACK_LIMITS.replyMax}`}>
        <textarea
          className="input min-h-16"
          maxLength={FEEDBACK_LIMITS.replyMax}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      </Field>
      {!isPublic && <p className="text-2xs text-warn">Gönderi panoda değil; yorum onaylanınca görünür.</p>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          loading={write.isPending}
          disabled={text.length < FEEDBACK_LIMITS.commentMin}
          onClick={() => write.mutate(text)}
        >
          Yorum yaz
        </Button>
      </div>
    </div>
  );
}

/** "Bildirilen yorumlar" sekmesi: bildirimi açık ya da gizlenmiş yorumlar. */
function FlaggedComments({ appId, onOpenPost }: { appId: string; onOpenPost: (postId: string) => void }): ReactNode {
  const queryClient = useQueryClient();
  const flagged = useQuery({
    queryKey: ["feedback-flagged", appId],
    queryFn: () => client.flaggedFeedbackComments(appId),
    refetchInterval: 60_000,
  });
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["feedback-flagged", appId] });
    void queryClient.invalidateQueries({ queryKey: ["feedback-comments", appId] });
    void queryClient.invalidateQueries({ queryKey: ["feedback", appId] });
  };
  const items = flagged.data?.items ?? [];
  return (
    <Card
      title={flagged.data ? `${items.length} yorum` : "Bildirilen yorumlar"}
      hint={`${FEEDBACK_LIMITS.commentHideReports} farklı bildirimde kendiliğinden gizlenir · son bildirilen üstte`}
      flush
    >
      {flagged.isError ? (
        <ErrorState error={flagged.error} onRetry={() => void flagged.refetch()} />
      ) : flagged.isLoading ? (
        <Empty>Yükleniyor…</Empty>
      ) : items.length ? (
        <div className="grid divide-y divide-neutral-900">
          {items.map((c) => (
            <CommentItem key={c.id} appId={appId} c={c} showPost onOpenPost={onOpenPost} onChanged={refresh} />
          ))}
        </div>
      ) : (
        <Empty>Bildirilen yorum yok</Empty>
      )}
    </Card>
  );
}
